// admin.tsx with permission filtering applied (temporary testing version)

import { lazy, Suspense, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import { red } from '@mui/material/colors';
import { Outlet } from 'react-router-dom';

import { AuthSplitLayout } from 'src/layouts/auth-split';
import { LanguagePopover } from 'src/layouts/components/language-popover';
import { allLangs } from 'src/locales';
import { Iconify } from 'src/components/iconify';
import { pxToRem } from 'src/theme/styles';
import { NotificationsDrawer } from 'src/layouts/components/notifications-drawer';
import CustomLink from 'src/components/custom-link/link';
import { Searchbar } from 'src/layouts/components/searchbar';
import { AccountDrawer } from 'src/layouts/components/account-drawer';

// --------------------------------------------------------------
import { usePermissions } from 'src/context/PermissionsContext';
import { hasAnyPermission, getModuleViewPermission, arePermissionsLoaded } from 'src/utils/permission-helper';
import { SplashScreen } from 'src/components/loading-screen';

// PermissionGuard component - inline to avoid import issues
type PermissionGuardProps = {
  children: React.ReactNode;
  requiredPermissions: string[];
  moduleName?: string;
};

function PermissionGuard({ 
  children, 
  requiredPermissions,
  moduleName 
}: PermissionGuardProps) {
  const { permissions } = usePermissions();

  const noPermissionsRequired = !requiredPermissions || requiredPermissions.length === 0;
  const permissionsLoaded = arePermissionsLoaded(permissions);

  const hasAccess = noPermissionsRequired 
    ? true 
    : permissionsLoaded && hasAnyPermission(requiredPermissions, permissions);
  
  const modulePermission = moduleName ? getModuleViewPermission(moduleName) : null;
  const hasModuleAccess = modulePermission && permissionsLoaded
    ? hasAnyPermission([modulePermission], permissions)
    : false;

  useEffect(() => {
    if (!permissionsLoaded) return;
    if (noPermissionsRequired) return;
    
    if (!hasAccess && !hasModuleAccess) {
      window.location.replace('/403');
    }
  }, [hasAccess, hasModuleAccess, noPermissionsRequired, permissionsLoaded]);

  if (noPermissionsRequired) {
    return <>{children}</>;
  }
  
  if (!permissionsLoaded) {
    return <SplashScreen />;
  }
  
  if (!hasAccess && !hasModuleAccess) {
    return <SplashScreen />;
  }
  
  return <>{children}</>;
}

// --------------------------------------------------------------
export const getAdminRoutes = (
  pathname: string,
  token: any,
  settings: any,
  mode: any,
  setMode: any,
  bannerData: any,
  moduleList: any[],
  hasPermission: any,
  bgImages?: { bg_image?: string }
) => {
  const adminComponentMap: {
    [key: string]: {
      component: () => Promise<{ default: React.ComponentType<any> }>;
      children?: {
        [childUrl: string]: () => Promise<{ default: React.ComponentType<any> }>;
      };
    };
  } = {
    profile: {
      component: () => import('src/pages/admin/profile/profile'),
    },
    dashboard: {
      component: () => import('src/pages/admin/dashboards'),
    },
    entities: {
      component: () => import('src/pages/admin/entities'),
      children: {
        'submodule/:sub_module': () => import('src/pages/admin/submodule-page'),
      },
    },
    properties: {
      component: () => import('src/pages/admin/properties'),
      children: {
        'submodule/:sub_module': () => import('src/pages/admin/submodule-page'),
        'submodule/add-property': () => import('src/pages/admin/properties/AddProperty'),
      },
    },
    conversations: {
      component: () => import('src/pages/admin/conversations'),
    },
    'access-logs': {
      component: () => import('src/pages/admin/access-logs'),
    },
    'business-control-panel': {
      component: () => import('src/pages/admin/app-management'),
    },
    'control-panel': {
      component: () => import('src/pages/admin/control-panel'),
    },
  };

  // ------------------ Sidebar Links ------------------
  const adminLinks = moduleList.flatMap((section) =>
    section.items
      .filter((item: any) => item.enabled !== false && hasPermission(item))
      .map((item: any) => ({
        label: item.name,
        url: `/${item.url}`,
      }))
  );

  // ------------------ Routes ------------------
  // NOTE: We don't filter by hasPermission here anymore - PermissionGuard will handle it
  // This allows direct URL access to be properly handled (redirect to 403 instead of 404)
  const routeChildren = moduleList.flatMap((section) =>
    section.items
      .filter((item: any) => item.enabled !== false)
      .flatMap((item: any, i: number) => {
        const config = adminComponentMap[item.url];
        if (!config) return [];

        const MainComponent = lazy(config.component);

        // Get required permissions from item config
        const requiredPermissions = item.checkPermissions || [];
        
        const mainRoute = {
          path: item.url,
          element: (
            <PermissionGuard 
              requiredPermissions={requiredPermissions}
              moduleName={item.url}
            >
              <Suspense key={`${item.url}-${i}`}>
                <MainComponent />
              </Suspense>
            </PermissionGuard>
          ),
        };

        // ---------- Sub Routes with permission check ----------
        // NOTE: We don't filter by hasPermission here - PermissionGuard will handle it
        const childRoutes =
          item.hasListMenuItems && Array.isArray(item.listMenuItems) && config.children
            ? item.listMenuItems
                .filter((sub: any) => sub.enabled !== false)
                .map((sub: any, j: number) => {
                  const subKey = sub.url.replace(/^\/+/, '');
                  const subImportFunc = config.children?.[subKey];
                  if (!subImportFunc) return null;

                  const SubComponent = lazy(subImportFunc);
                  // Get required permissions from sub item config
                  const subRequiredPermissions = sub.checkPermissions || [];
                  
                  return {
                    path: `${item.url}${sub.url}`,
                    element: (
                      <PermissionGuard 
                        requiredPermissions={subRequiredPermissions}
                        moduleName={item.url}
                      >
                        <Suspense key={`${item.url}${sub.url}-${j}`}>
                          <SubComponent />
                        </Suspense>
                      </PermissionGuard>
                    ),
                  };
                })
                .filter(Boolean)
            : [];

        return [mainRoute, ...childRoutes];
      })
  );

  // ------------------ Header Area ------------------
  const headerConfig = moduleList.find((section) => section?.header)?.header || {};
  const centerAreaItems = headerConfig?.centerArea || [];
  const rightAreaItems = headerConfig?.rightArea || [];

  // ------------------ Account Drawer ------------------
  const accountDrawerItems =
    moduleList.find((section) => section.accountDrawer)?.accountDrawer || [];

  const account_data = accountDrawerItems
    .filter((item: any) => item.enabled !== false && hasPermission(item))
    .map((item: any) => ({
      label: item.label,
      href: item.href,
      icon: <Iconify icon={item.icon} />,
    }));

  // --------------------------------------------------------------
  return [
    {
      element: (
        <AuthSplitLayout
          section={{
            bgImage: bgImages?.bg_image || '/assets/background/mountains_bg_light.png',
            imgUrl: false,
            renderBackButton: false,
            showLogout: true,
            customSection: (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                  position: 'sticky',
                  top: 0,
                }}
              >
                {adminLinks.map((path, idx) => (
                  <CustomLink
                    key={`${path.label}-${idx}`}
                    to={path.url}
                    label={path.label}
                    style={{ color: pathname.includes(path.url) ? '#ffd400' : 'white' }}
                  />
                ))}
              </Box>
            ),
          }}
          header={{
            slots: {
              leftArea: <></>,
              centerArea: (
                <Box display="flex" alignItems="flex-end">
                  {centerAreaItems
                    .filter((item: any) => item?.enabled !== false && hasPermission(item))
                    .map((item: any, idx: number) => {
                      if (item.type === 'searchbar') return <Searchbar key={`center-${idx}`} />;
                      return null;
                    })}
                </Box>
              ),
              rightArea: (
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
                  {rightAreaItems
                    .filter((item: any) => item?.enabled !== false && hasPermission(item))
                    .map((item: any, idx: number) => {
                      switch (item.type) {
                        case 'link':
                          return (
                            <CustomLink
                              key={`link-${idx}`}
                              to={item.href}
                              target={item.target}
                              label={item.label}
                              style={{
                                fontWeight: 600,
                                lineHeight: 22 / 14,
                                fontSize: pxToRem(14),
                                color: 'inherit',
                              }}
                            />
                          );
                        case 'notifications':
                          return <NotificationsDrawer key={`notifications-${idx}`} item={item} />;
                        case 'language-switcher':
                          return <LanguagePopover key={`localization-${idx}`} data={allLangs} />;
                        case 'theme-toggle':
                          return (
                            <IconButton
                              key={`theme-${idx}`}
                              onClick={() => {
                                settings.onUpdateField(
                                  'colorScheme',
                                  mode === 'light' ? 'dark' : 'light'
                                );
                                setMode(mode === 'light' ? 'dark' : 'light');
                              }}
                            >
                              <Iconify
                                icon={
                                  mode === 'dark'
                                    ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                                    : 'line-md:moon-rising-filled-loop'
                                }
                                height={48}
                                color={mode === 'dark' ? red[400] : '-moz-initial'}
                              />
                            </IconButton>
                          );
                        case 'admin-profile':
                          return <AccountDrawer data-slot="account" data={account_data} />;
                        default:
                          return null;
                      }
                    })}
                </Box>
              ),
              drawerHeader: (
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
                  {rightAreaItems
                    .filter((item: any) => item?.enabled !== false && hasPermission(item))
                    .map((item: any, idx: number) => {
                      switch (item.type) {
                        case 'notifications':
                          return <NotificationsDrawer key={`notifications-${idx}`} item={item} />;
                        case 'language-switcher':
                          return <LanguagePopover key={`localization-${idx}`} data={allLangs} />;
                        case 'theme-toggle':
                          return (
                            <IconButton
                              key={`theme-${idx}`}
                              onClick={() => {
                                settings.onUpdateField(
                                  'colorScheme',
                                  mode === 'light' ? 'dark' : 'light'
                                );
                                setMode(mode === 'light' ? 'dark' : 'light');
                              }}
                            >
                              <Iconify
                                icon={
                                  mode === 'dark'
                                    ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                                    : 'line-md:moon-rising-filled-loop'
                                }
                                height={48}
                                color={mode === 'dark' ? red[400] : '-moz-initial'}
                              />
                            </IconButton>
                          );
                        default:
                          return null;
                      }
                    })}
                </Box>
              ),
            },
          }}
        >
          <Box sx={{ width: '100%', height: '88vh' }}>
            <Outlet />
          </Box>
        </AuthSplitLayout>
      ),
      children: [
        {
          children: routeChildren,
        },
      ],
    },
  ];
};
